import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag } from "lucide-react";
import { api } from "../lib/api";

const STEPS = ["confirmed", "building", "shipped", "delivered"];
const STEP_LABELS = {
  confirmed: "Order Confirmed",
  building: "Building Your Frame",
  shipped: "Shipped",
  delivered: "Delivered",
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

export default function OrdersPage() {
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [orders, setOrders] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setOrders(null);
    setLoading(true);
    try {
      const { data } = await api.post("/orders/my", {
        email: email.trim(),
        phone: phone.trim(),
      });
      setOrders(data.orders);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Could not find orders. Check your details.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white px-6 md:px-12 py-16">
      <div className="max-w-3xl mx-auto">
        <p className="af-mono text-[10px] uppercase tracking-[0.25em] text-[#FF2800]">
          // Garage Records
        </p>
        <h1 className="af-display text-4xl sm:text-5xl font-black uppercase tracking-tight mt-2">
          My Orders
        </h1>
        <p className="text-zinc-400 mt-3 text-sm">
          Enter the email and phone number you used at checkout to see all your orders.
        </p>

        <form onSubmit={submit} className="mt-8 grid sm:grid-cols-2 gap-4" data-testid="my-orders-form">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            required
            className="w-full bg-black border border-white/15 px-4 py-3 text-sm focus:outline-none focus:border-[#FF2800] transition-colors placeholder:text-zinc-700"
            data-testid="my-orders-email-input"
          />
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="Phone used at checkout"
            required
            className="w-full bg-black border border-white/15 px-4 py-3 text-sm focus:outline-none focus:border-[#FF2800] transition-colors placeholder:text-zinc-700"
            data-testid="my-orders-phone-input"
          />
          <button
            type="submit"
            disabled={loading}
            className="sm:col-span-2 sm:w-max inline-flex items-center gap-2 bg-[#FF2800] text-white font-semibold uppercase tracking-wider px-8 py-3.5 hover:bg-[#CC2000] disabled:opacity-50 transition-colors text-sm"
            data-testid="my-orders-submit-button"
          >
            <ShoppingBag size={16} />
            {loading ? "Searching…" : "Show my orders"}
          </button>
        </form>

        {error && (
          <p className="mt-6 text-[#FF2800] text-sm" data-testid="my-orders-error">
            {error}
          </p>
        )}

        {orders && (
          <div className="mt-10 space-y-4" data-testid="my-orders-list">
            <p className="af-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
              {orders.length} order{orders.length > 1 ? "s" : ""} found
            </p>
            {orders.map((o) => {
              const idx = STEPS.indexOf(o.order_status);
              return (
                <div
                  key={o.order_id}
                  className="border border-white/10 bg-[#0E0E0E] p-6"
                  data-testid={`my-order-${o.order_id}`}
                >
                  <div className="flex items-start justify-between flex-wrap gap-3">
                    <div>
                      <p className="af-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                        {fmtDate(o.created_at)}
                      </p>
                      {(o.items || []).map((it, i) => (
                        <p key={i} className="text-sm text-zinc-200 mt-1">
                          {it.name} × {it.qty}
                          {it.options_label ? (
                            <span className="text-zinc-500"> · {it.options_label}</span>
                          ) : null}
                        </p>
                      ))}
                    </div>
                    <p className="af-display text-xl font-bold">
                      ₹{Number(o.amount).toLocaleString("en-IN")}
                    </p>
                  </div>

                  <div className="mt-5 flex items-center gap-1.5" data-testid="my-order-progress">
                    {STEPS.map((s, i) => (
                      <div key={s} className="flex-1">
                        <div
                          className={`h-1.5 ${i <= idx ? "bg-[#FF2800]" : "bg-zinc-800"}`}
                        />
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 flex items-center justify-between flex-wrap gap-2">
                    <p className="af-mono text-[10px] uppercase tracking-[0.2em] text-[#FF2800]">
                      {STEP_LABELS[o.order_status] || o.order_status}
                    </p>
                    {o.tracking_number && (
                      <p className="af-mono text-[10px] text-zinc-400">
                        {o.courier ? `${o.courier} · ` : ""}#{o.tracking_number}
                      </p>
                    )}
                  </div>

                  <div className="mt-4 pt-3 border-t border-white/10 flex items-center justify-between flex-wrap gap-2">
                    <p className="af-mono text-[10px] text-zinc-600 break-all">{o.order_id}</p>
                    <Link
                      to="/track"
                      className="af-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 hover:text-white transition-colors"
                      data-testid="my-order-track-link"
                    >
                      Full tracking →
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
